import React, { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaEdit, FaCheck, FaTimes, FaExclamation } from 'react-icons/fa';
import axios from 'axios';
import config from '../../config';

const API_BASE_URL = `${config.API_BASE_URL}/api/study-tools`;

const TaskManager = () => {
  const [tasks, setTasks] = useState([]);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    dueDate: '',
    priority: 'medium',
    subject: '',
    progress: 0
  });
  const [filterStatus, setFilterStatus] = useState('pending');

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/tasks`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const handleAddTask = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_BASE_URL}/tasks`, newTask, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTasks([...tasks, { ...newTask, id: response.data.id }]);
      setNewTask({
        title: '',
        description: '',
        dueDate: '',
        priority: 'medium',
        subject: '',
        progress: 0
      });
      setShowAddForm(false);
    } catch (error) {
      console.error('Error creating task:', error);
    }
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setNewTask(task);
    setShowAddForm(true);
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_BASE_URL}/tasks/${editingTask.id}`, newTask, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTasks(tasks.map(task => 
        task.id === editingTask.id ? { ...newTask, id: task.id } : task
      ));
      setNewTask({
        title: '',
        description: '',
        dueDate: '',
        priority: 'medium',
        subject: '',
        progress: 0
      });
      setEditingTask(null);
      setShowAddForm(false);
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await axios.delete(`${API_BASE_URL}/tasks/${taskId}`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTasks(tasks.filter(task => task.id !== taskId));
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const handleToggleComplete = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    try {
      await axios.put(`${API_BASE_URL}/tasks/${taskId}`, {
        ...task,
        completed: !task.completed
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTasks(tasks.map(t =>
        t.id === taskId ? { ...t, completed: !t.completed } : t
      ));
    } catch (error) {
      console.error('Error updating task completion:', error);
    }
  };

  const handleProgressChange = async (taskId, progress) => {
    const task = tasks.find(t => t.id === taskId);
    try {
      await axios.put(`${API_BASE_URL}/tasks/${taskId}`, {
        ...task,
        progress
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTasks(tasks.map(t =>
        t.id === taskId ? { ...t, progress } : t
      ));
    } catch (error) {
      console.error('Error updating task progress:', error);
    }
  };

  const handleMarkComplete = async (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    if (task.completed) return;
    try {
      await axios.put(`${API_BASE_URL}/tasks/${taskId}`, {
        ...task,
        completed: true
      }, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setTasks(tasks.map(t =>
        t.id === taskId ? { ...t, completed: true } : t
      ));
    } catch (error) {
      console.error('Error marking task as complete:', error);
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high':
        return <FaExclamation className="priority-icon high" />;
      case 'medium':
        return <FaExclamation className="priority-icon medium" />;
      case 'low':
        return <FaExclamation className="priority-icon low" />;
      default:
        return null;
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'completed') return task.completed;
    if (filterStatus === 'pending') return !task.completed;
    return true;
  });

  return (
    <div className="task-manager">
      <div className="task-header">
        <h2>Tasks & Goals</h2>
        <button 
          className="add-task-button"
          onClick={() => setShowAddForm(true)}
        >
          <FaPlus /> Add New Task
        </button>
      </div>

      {showAddForm && (
        <div className="task-form-overlay">
          <div className="task-form">
            <h3>{editingTask ? 'Edit Task' : 'Add New Task'}</h3>
            <form onSubmit={editingTask ? handleUpdateTask : handleAddTask}>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={newTask.title}
                  onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={newTask.description}
                  onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Due Date</label>
                <input
                  type="date"
                  value={newTask.dueDate}
                  onChange={(e) => setNewTask({ ...newTask, dueDate: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Priority</label>
                <select
                  value={newTask.priority}
                  onChange={(e) => setNewTask({ ...newTask, priority: e.target.value })}
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>

              <div className="form-group">
                <label>Subject</label>
                <input
                  type="text"
                  value={newTask.subject}
                  onChange={(e) => setNewTask({ ...newTask, subject: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Progress ({newTask.progress}%)</label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={newTask.progress}
                  onChange={(e) => setNewTask({ ...newTask, progress: parseInt(e.target.value) })}
                />
              </div>

              <div className="form-actions">
                <button type="submit" className="save-button">
                  {editingTask ? 'Update Task' : 'Add Task'}
                </button>
                <button 
                  type="button" 
                  className="cancel-button"
                  onClick={() => {
                    setShowAddForm(false);
                    setEditingTask(null);
                    setNewTask({
                      title: '',
                      description: '',
                      dueDate: '',
                      priority: 'medium',
                      subject: '',
                      progress: 0
                    });
                  }}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="task-filters">
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="all">All Tasks</option>
          <option value="completed">Completed</option>
          <option value="pending">Pending</option>
        </select>

        <select defaultValue="dueDate">
          <option value="dueDate">Sort by Due Date</option>
          <option value="priority">Sort by Priority</option>
          <option value="progress">Sort by Progress</option>
        </select>
      </div>

      <div className="task-list">
        {filteredTasks.map(task => (
          <div key={task.id} className={`task-item ${task.completed ? 'completed' : ''}`}>
            <div className="task-checkbox" onClick={() => handleToggleComplete(task.id)}>
              {task.completed ? <FaCheck /> : null}
            </div>

            <div className="task-content">
              <div className="task-header">
                <h4 className="task-title">
                  {getPriorityIcon(task.priority)} {task.title}
                </h4>
                <div className="task-actions">
                  <button onClick={() => handleEditTask(task)}>
                    <FaEdit />
                  </button>
                  <button onClick={() => handleDeleteTask(task.id)}>
                    <FaTrash />
                  </button>
                  {!task.completed && (
                    <button onClick={() => handleMarkComplete(task.id)}>
                      <FaCheck /> Complete
                    </button>
                  )}
                </div>
              </div>

              <p className="task-description">{task.description}</p>

              <div className="task-meta">
                <span>Due: {task.dueDate}</span>
                <span>Subject: {task.subject}</span>
                <span className={`task-priority priority-${task.priority}`}>
                  {task.priority.charAt(0).toUpperCase() + task.priority.slice(1)}
                </span>
              </div>

              <div className="task-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill"
                    style={{ width: `${task.progress}%` }}
                  ></div>
                </div>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={task.progress}
                  onChange={(e) => handleProgressChange(task.id, parseInt(e.target.value))}
                />
                <span className="progress-label">{task.progress}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TaskManager; 